import { Injectable } from '@angular/core';
import { GeneralComponent } from '../General/g.component';
import { TopicService } from './topic.service';

@Injectable({
  providedIn: 'root'
})
export class SelfcorrectingService extends GeneralComponent {

  constructor(topicService: TopicService) {
    super(topicService);
  }

  /**
   * returns all possible codes of given length
   * @param length - length of codes
   */
  allCodes(length: number): string[] {
    return this.createArray(Math.pow(2, length)).map(v => ("0".repeat(length).concat(v.toString(2))).slice(-length));
  }

  /**
   * computes the distance between the given codes
   * @param code1 
   * @param code2 
   */
  distance(code1: string, code2: string): number {
    return code1.split("").filter((v, i) => v != code2.charAt(i)).length;
  }

  /**
   * returns all codes with distance smaller or equal than the given distance to the given code
   * @param length - length of codes
   * @param distance - maximal distance
   * @param code - code
   */
  distanceSmallerEqual(length: number, distance: number, code: string): string[] {
    console.log(distance);
    console.log(this.allCodes(length).map(v => this.distance(v, code)));
    return this.allCodes(length).filter((v) => this.distance(v, code) <= distance && this.distance(v, code) != 0);
  }

  /**
   * returns all codes with distance larger than the given distance to the given code
   * @param length - length of codes
   * @param distance - minimal distance
   * @param code - code
   */
  distanceLarger(length: number, distance: number, code: string): string[] {
    return this.allCodes(length).filter((v) => this.distance(v, code) > distance);
  }

  /** return the minimal distance within a set of codes */
  minDistance(values: string[]) {
    let pairs = [];
    for (let i = 0; i < values.length; i++) {
      for (let j = 0; j < i; j++) {
        pairs.push([values[i], values[j]]);
      }
    }
    return Math.min(...pairs.map(v => this.distance(v[0], v[1])));
  }

  /** computes the hamming code for a given message */
  computeHamming(message: string): string {
    if (!message) {
      return "";
    }
    let base = 0;
    for (let i = 0; i < 15; i++) {
      if (this.powerOfTwo(i)) {
        continue;
      }
      if (message.charAt(message.length - i - 1) != "0") {
        base = base ^ (i + 1);
      }
    }
    let length = Math.ceil(Math.log2(message.length));
    return "0".repeat(length).concat(base.toString(2)).slice(-length);
  }

  /** computes the control bits for the card trick
   * @param messageArray - message in right layout
   * @param dimension - dimension of the message array
   */
  controlBits(messageArray: number[][], dimension: number[]): number[] {
    let index = 0;
    let c = [];
    for (let i = 0; i < dimension[1]; i++) {
      let col = messageArray.map(v => v[i]);
      let sum = col.map(v => + v).reduce((a, b) => a + b, 0);
      c[index] = sum % 2;
      index++;
    }
    
    for (let row of messageArray) {
      let sum = row.map(v => + v).reduce((a, b) => a + b, 0);
      c[index] = sum % 2;
      index++;
    }
    let sum = c.slice(0, dimension[1]).map(v => + v).reduce((a, b) => a + b, 0);
    c[index] = sum % 2;
    index++;
    return c;
  }

  /** reconstruct the correct message given the wrong message, the method and optional the dimension 
   * @param message - message to correct
   * @param method - cardTrick or hamming
   * @param dimension - sice of card trick
  */
  reconstruct(message: string, method: string, dimension?: number[]): string {
    if (method == "cardTrick") {
      let oldC = message.slice(-dimension[0] - dimension[1] - 1);
      let messageArray = [];
      let index = 0;
      for (let i = 0; i < dimension[0]; i++) {
        let elem = [];
        for (let j = 0; j < dimension[1]; j++) {
          elem[j] = message.charAt(index);
          index++;
        }
        messageArray.push(elem);
      }
      let newC = this.controlBits(messageArray, dimension);
      let row = -1;
      let col = -1;
      for (let i = 0; i < dimension[1]; i++) {
        if (newC[i] != +oldC.charAt(i)) {
          col = i;
        }
      }
      for (let i = 0; i < dimension[0]; i++) {
        if (newC[i + dimension[1]] != +oldC.charAt(i + dimension[1])) {
          row = i;
        }
      }
      let flippedBit = row * dimension[1] + col;
      message = message.substr(0, flippedBit).concat((1 - +message.charAt(flippedBit)).toString(), message.substr(flippedBit + 1));
    } else {
      let p = [];
      p[0] = "";
      p[1] = "";
      p[2] = "";
      p[3] = "";
      for (let i of this.allCodes(4)) {
        if (i != "0000") {
          for (let j = 0; j < 4; j++) {
            if (i.charAt(j) == "1") {
              p[3 - j] = p[3 - j].concat(message.charAt(parseInt(i, 2) - 1), " ");
            }
          }
        }
      }
      let c = p.map(v => v.split("").map(v => +v).reduce((a, b) => a + b, 0)).map(v => v % 2).map((v,i) => v * Math.pow(2,i)).reduce((a,b) => a+b, 0)-1;
      message = message.substr(0, c).concat((1 - +message.charAt(c)).toString(), message.substr(c + 1));
    }
    return message;
  }

  /** returns the checkbit of the EAN-Code  */
  eanCheckBit(code: string): string {
    code = code.substr(0, 12);
    let result = 0;
    let index = 0;
    for (let i of code) {
      result += +i;
      if (index % 2 != 0) {
        result += 2 * +i;
      }
      index++;
    }

    result = 10 - (result % 10);
    if (+result == 10) {
      return "0";
    }
    return result.toString();
  }

  /** returns the checkBit of the ISBN-Code */
  isbnCheckBit(code: string): string {
    code = code.substr(0, 9);
    let result = 0;
    let index = 1;
    for (let i of code) {
      result += index * +i;
      index++;
    }

    result = (result % 11);
    if (+result == 10) {
      return "X";
    }
    return result.toString();
  }

  /** checks whether x is a power of 2
   * @param x - number to check
   */
  powerOfTwo(x: number): boolean {
    return (Math.log(x) / Math.log(2)) % 1 === 0
  }
}
